import { useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BottomSheetRef } from 'react-spring-bottom-sheet';
import { ReportSuccess } from '~/components/Report';
import { Input } from '~/components/atomic';
import { DefaultButton } from '~/components/atomic';
import { useSendChatReportMutation } from '~/features/chatRoom';
import { useModal } from '~/hooks';
import { SendChatReportRequest } from '~/types';
import { useUserStore } from '~/store';
import { InputTextarea } from '../..';
import BottomSheetContainer from '../BottomSheet';
import * as S from './ChatReport.styled';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const ChatReportBottomSheet = ({ isOpen, onClose }: Props) => {
  const { id: chatroom_id } = useParams();
  const email = useUserStore((state) => state.email);

  const bottomSheetRef = useRef<BottomSheetRef>(null);

  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const { mutate, isPending } = useSendChatReportMutation();

  const [subject, setSubject] = useState<string>('');
  const [explanation, setExplanation] = useState<string>('');

  const hasUnsavedChanges: boolean = Boolean(subject) || Boolean(explanation);

  const { openModal } = useModal();

  const handleSubmit = () => {
    if (!chatroom_id) return;
    const requestData: SendChatReportRequest = {
      chatroom_id,
      subject,
      explanation,
    };
    mutate(requestData, {
      onSuccess({ status }) {
        setSubject('');
        setExplanation('');
        setIsSubmitted(true);
        bottomSheetRef.current?.snapTo(1);
      },
    });
  };

  const onCloseBottomSheet = () => {
    if (hasUnsavedChanges) {
      openModal({
        title: 'Leave page',
        content:
          'If you leave, the current content will be deleted. Do you want to leave this page?',
        type: 'double_red',
        buttonName: 'Leave',
        click: () => {
          setSubject('');
          setExplanation('');
          onClose();
        },
        cancelClick: () => {
          bottomSheetRef.current?.snapTo(0.8);
        },
      });
    } else {
      onClose();
    }
  };

  return (
    <BottomSheetContainer
      isOpen={isOpen}
      openHeightRates={[0.8, 1]}
      blocking
      disableContentDrag={true}
      onClose={onCloseBottomSheet}
      ref={bottomSheetRef}
    >
      {isSubmitted ? (
        <ReportSuccess onClickConfirm={onClose} />
      ) : (
        <S.Container>
          <S.Title>Report</S.Title>
          <Input
            id='ChatReportSubject'
            label='Subject'
            placeholder='Report your problem here shortly'
            text={subject}
            setText={setSubject}
            textLengthOpt
          />
          <InputTextarea
            id='ChatReportExplanation'
            label='Explanation'
            placeholder='Please give us more details'
            text={explanation}
            setText={setExplanation}
            textLengthOpt
            maxText={1200}
            rows={3}
          />
          <DefaultButton
            disabled={subject.trim() === '' || explanation.trim() === ''}
            onClick={handleSubmit}
            isLoading={isPending}
          >
            Submit
          </DefaultButton>
        </S.Container>
      )}
    </BottomSheetContainer>
  );
};

export default ChatReportBottomSheet;
